import { useCallback, useEffect, useRef, useState } from 'react';
import { ClientMessage, ServerMessage, ConnectionStatus } from '../types/simulation';

interface UseWebSocketReturn {
  connect: () => void;
  disconnect: () => void;
  send: (message: ClientMessage) => void;
  sendBinary: (data: ArrayBuffer) => void;
  status: ConnectionStatus;
  lastMessage: ServerMessage | null;
  error: string | null;
}

export function useWebSocket(url: string): UseWebSocketReturn {
  const [status, setStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState<ServerMessage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 second

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus(ConnectionStatus.DISCONNECTED);
    reconnectAttemptsRef.current = 0;
  }, []);

  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Clear any existing connection first
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      setStatus(ConnectionStatus.CONNECTING);
      setError(null);
      reconnectAttemptsRef.current = 0; // Reset reconnect counter on manual connect

      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';

      ws.onopen = () => {
        console.log('WebSocket opened');
        setStatus(ConnectionStatus.CONNECTED);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          // Handle binary frames (audio data)
          if (event.data instanceof ArrayBuffer) {
            const data = new Uint8Array(event.data);

            if (data.length < 1) return;

            const msgType = data[0];

            // MSG_TYPE_AUDIO_INPUT = 0x02
            if (msgType === 0x02) {
              // Audio input: [1 byte type][8192 bytes audio (2048 float32)]
              const expectedSize = 1 + 2048 * 4;
              if (data.length !== expectedSize) {
                console.error(`Invalid binary audio frame size: ${data.length}, expected ${expectedSize}`);
                return;
              }

              // Extract audio samples (skip first byte)
              // Copy to aligned buffer since offset 1 is not a multiple of 4
              const audioBytes = event.data.slice(1);
              const audioData = new Float32Array(audioBytes);
              const samples = Array.from(audioData);

              setLastMessage({
                type: 'audio_input',
                samples,
              });
            } else {
              console.warn('Unknown binary message type:', msgType);
            }
          }
          // Handle JSON frames (control messages)
          else if (typeof event.data === 'string') {
            const message: ServerMessage = JSON.parse(event.data);
            setLastMessage(message);

            // Handle successful join
            if (message.type === 'joined') {
              console.log('Authentication successful');
            }

            // Handle error messages
            if (message.type === 'error') {
              console.error('Server error:', message.message);
              setError(message.message);
              setStatus(ConnectionStatus.ERROR);
              // Prevent reconnection before closing
              reconnectAttemptsRef.current = maxReconnectAttempts;
              // Close the connection on auth errors
              if (wsRef.current) {
                wsRef.current.close();
              }
            }
          }
        } catch (err) {
          console.error('Failed to parse message:', err);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          setStatus(ConnectionStatus.DISCONNECTED);
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else {
          // Only set generic error if no specific error was already set
          setError((prevError) => prevError || 'Failed to connect after multiple attempts');
          setStatus(ConnectionStatus.ERROR);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
        setStatus(ConnectionStatus.ERROR);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setStatus(ConnectionStatus.ERROR);
    }
  }, [url]);

  const send = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
      } catch (err) {
        console.error('Failed to send message:', err);
        setError(err instanceof Error ? err.message : 'Failed to send message');
      }
    } else {
      console.error('WebSocket not connected');
      setError('Not connected to server');
    }
  }, []);

  const sendBinary = useCallback((data: ArrayBuffer) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(data);
      } catch (err) {
        console.error('Failed to send binary message:', err);
        setError(err instanceof Error ? err.message : 'Failed to send binary message');
      }
    }
    // Silently ignore if not connected - handled by App.tsx connection check
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    send,
    sendBinary,
    status,
    lastMessage,
    error,
  };
}
